import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, X } from "lucide-react";
import { api } from "@/lib/api";

const PROD_JS_URL = "https://gateway.moneris.com/chkt/js/chkt_v1.00.js";
const QA_JS_URL = "https://gatewayt.moneris.com/chkt/js/chkt_v1.00.js";

let mcoScriptPromise = null;
function loadMcoScript(jsUrl) {
    // NOTE: a DOM element id can shadow window.monerisCheckout (named globals),
    // so we must check for the constructor function specifically.
    if (typeof window.monerisCheckout === "function") return Promise.resolve();
    if (!mcoScriptPromise) {
        mcoScriptPromise = new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = jsUrl;
            s.async = true;
            s.onload = resolve;
            s.onerror = () => {
                mcoScriptPromise = null;
                reject(new Error("Could not load Moneris Checkout"));
            };
            document.body.appendChild(s);
        });
    }
    return mcoScriptPromise;
}

/**
 * Renders the Moneris Checkout (MCO) hosted payment module in a full-screen
 * overlay. Card entry happens inside Moneris' iframe — we never see card data.
 * After payment_complete, the backend Receipt API is the source of truth.
 */
export const MonerisCheckoutModal = ({ ticket, environment, jsUrl, bookingId, onClose }) => {
    const navigate = useNavigate();
    const mcoRef = useRef(null);
    const finishedRef = useRef(false);
    const [phase, setPhase] = useState("loading"); // loading | ready | verifying

    useEffect(() => {
        let disposed = false;
        const src = jsUrl || (environment === "qa" ? QA_JS_URL : PROD_JS_URL);

        async function verifyPayment(cbTicket) {
            setPhase("verifying");
            try {
                const { data } = await api.post("/payments/checkout/receipt", {
                    booking_id: bookingId,
                    ticket: cbTicket || ticket,
                });
                teardown();
                if (data.approved) {
                    navigate(
                        `/payment-success?provider=moneris_checkout&reference=${encodeURIComponent(
                            data.reference_id || ""
                        )}&txn=${encodeURIComponent(data.txn_number || "")}`
                    );
                } else {
                    navigate(
                        `/payment-failed?reason=${encodeURIComponent(
                            data.message || "declined"
                        )}`
                    );
                }
            } catch (err) {
                teardown();
                navigate(
                    `/payment-failed?reason=${encodeURIComponent(
                        err?.response?.data?.detail || "verification_error"
                    )}`
                );
            }
        }

        function cancelPayment() {
            api.post("/payments/checkout/cancel", { booking_id: bookingId, ticket }).catch(() => {});
            teardown();
            navigate("/payment-cancelled");
        }

        function failPayment(reason) {
            teardown();
            navigate(`/payment-failed?reason=${encodeURIComponent(reason)}`);
        }

        function teardown() {
            if (finishedRef.current) return;
            finishedRef.current = true;
            try {
                mcoRef.current?.closeCheckout();
            } catch {
                /* already closed */
            }
        }

        loadMcoScript(src)
            .then(() => {
                if (disposed || mcoRef.current) return;
                const mco = new window.monerisCheckout();
                mcoRef.current = mco;
                mco.setMode(environment === "qa" ? "qa" : "prod");
                mco.setCheckoutDiv("mco-payment-div");
                mco.setCallback("page_loaded", () => setPhase("ready"));
                mco.setCallback("cancel_transaction", () => cancelPayment());
                mco.setCallback("error_event", (json) => {
                    let code = "checkout_error";
                    try {
                        code = `checkout_error_${JSON.parse(json).response_code || ""}`;
                    } catch {
                        /* keep default */
                    }
                    failPayment(code);
                });
                mco.setCallback("payment_receipt", () => {});
                mco.setCallback("payment_complete", (json) => {
                    let cbTicket = ticket;
                    try {
                        cbTicket = JSON.parse(json).ticket || ticket;
                    } catch {
                        /* keep preload ticket */
                    }
                    verifyPayment(cbTicket);
                });
                mco.startCheckout(ticket);
            })
            .catch((err) => {
                console.error("Moneris Checkout init failed:", err);
                failPayment(
                    typeof window.monerisCheckout === "function"
                        ? "checkout_init_error"
                        : "checkout_load_error"
                );
            });

        return () => {
            disposed = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticket]);

    function handleManualClose() {
        if (phase === "verifying") return;
        api.post("/payments/checkout/cancel", { booking_id: bookingId, ticket }).catch(() => {});
        try {
            mcoRef.current?.closeCheckout();
        } catch {
            /* already closed */
        }
        finishedRef.current = true;
        onClose?.();
        navigate("/payment-cancelled");
    }

    return (
        <div
            className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-[#0E2A47]/70 px-4 py-8 backdrop-blur-sm"
            data-testid="moneris-checkout-overlay"
        >
            <div className="relative w-full max-w-lg rounded-3xl bg-white p-4 shadow-2xl sm:p-6">
                <div className="mb-3 flex items-center justify-between">
                    <p className="inline-flex items-center gap-2 text-sm font-semibold text-[#0E2A47]">
                        <ShieldCheck size={18} className="text-[#F26B1F]" />
                        Secure payment by Moneris
                    </p>
                    <button
                        type="button"
                        onClick={handleManualClose}
                        aria-label="Close payment"
                        data-testid="moneris-checkout-close"
                        className="rounded-full p-1.5 text-[#475569] transition-colors hover:bg-[#FAF6EE] hover:text-[#0E2A47]"
                    >
                        <X size={18} />
                    </button>
                </div>

                {phase !== "ready" && (
                    <div
                        className="flex flex-col items-center justify-center gap-3 py-14 text-[#475569]"
                        data-testid="moneris-checkout-status"
                    >
                        <Loader2 className="animate-spin text-[#F26B1F]" size={28} />
                        <p className="text-sm">
                            {phase === "verifying"
                                ? "Confirming your payment with Moneris…"
                                : "Loading secure checkout…"}
                        </p>
                    </div>
                )}

                <div id="mco-payment-div" className={phase === "verifying" ? "hidden" : ""} />
            </div>
        </div>
    );
};
